import React, { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import { AlertTriangle, CheckCircle2, Clock, ArrowUpRight } from "lucide-react";

const STATUSES = ["open", "in_progress", "resolved", "escalated"];
const STATUS_LABEL = { open: "Open", in_progress: "In progress", resolved: "Resolved", escalated: "Escalated" };
const STATUS_TONE = {
  open: "bg-rose-100 text-rose-700",
  in_progress: "bg-amber-100 text-amber-700",
  resolved: "bg-emerald-100 text-emerald-700",
  escalated: "bg-purple-100 text-purple-700",
};

export default function TicketsPage() {
  const [items, setItems] = useState([]);
  const [stats, setStats] = useState(null);
  const [filter, setFilter] = useState("all");
  const [openTicket, setOpenTicket] = useState(null);
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  const load = async () => {
    const [a, b] = await Promise.all([api.get("/tickets"), api.get("/tickets/stats")]);
    setItems(a.data);
    setStats(b.data);
  };
  useEffect(() => { load(); }, []);

  const visible = filter === "all" ? items : items.filter((t) => t.status === filter);

  const update = async (id, patch) => {
    setBusy(true); setErr("");
    try {
      const { data } = await api.put(`/tickets/${id}`, patch);
      setItems((arr) => arr.map((t) => (t.id === id ? data : t)));
      if (openTicket?.id === id) setOpenTicket(data);
      load();
    } catch (e) {
      setErr(formatApiError(e));
    } finally { setBusy(false); }
  };

  const addNote = async () => {
    if (!note.trim() || !openTicket) return;
    await update(openTicket.id, { note: note.trim() });
    setNote("");
  };

  return (
    <div className="space-y-5 fade-in">
      <div className="grid md:grid-cols-4 gap-4">
        <Stat data-testid="ticket-stat-open" label="Open" value={stats?.open ?? 0} icon={<AlertTriangle size={18}/>} tone="rose"/>
        <Stat data-testid="ticket-stat-progress" label="In progress" value={stats?.in_progress ?? 0} icon={<Clock size={18}/>} tone="amber"/>
        <Stat data-testid="ticket-stat-resolved" label="Resolved" value={stats?.resolved ?? 0} icon={<CheckCircle2 size={18}/>} tone="emerald"/>
        <Stat data-testid="ticket-stat-total" label="Total" value={stats?.total ?? 0} icon={<ArrowUpRight size={18}/>} tone="brand"/>
      </div>

      <Card className="p-5 border-slate-100">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div>
            <h2 className="font-extrabold text-[#1F3A6B] text-lg">Closed-loop tickets</h2>
            <p className="text-sm text-slate-600">Auto-created when a respondent gives a detractor score.</p>
          </div>
          <div className="flex flex-wrap gap-1.5">
            {["all", ...STATUSES].map((s) => (
              <button
                key={s}
                data-testid={`tickets-filter-${s}`}
                onClick={() => setFilter(s)}
                className={`text-xs px-3 py-1.5 rounded-full border transition ${
                  filter === s ? "bg-[#F58220] text-white border-[#F58220]" : "border-slate-200 text-slate-700 hover:border-[#F58220]"
                }`}
              >
                {s === "all" ? "All" : STATUS_LABEL[s]}
              </button>
            ))}
          </div>
        </div>
      </Card>

      {visible.length === 0 ? (
        <Card className="p-10 text-center border-dashed border-2 border-slate-200">
          <p className="font-bold text-[#1F3A6B]">No tickets here</p>
          <p className="text-sm text-slate-500 mt-1">When detractors respond, tickets land here automatically.</p>
        </Card>
      ) : (
        <div className="grid lg:grid-cols-3 gap-5">
          {/* List */}
          <div className="lg:col-span-2 space-y-3">
            {visible.map((t) => (
              <Card
                key={t.id}
                data-testid={`ticket-${t.id}`}
                onClick={() => setOpenTicket(t)}
                className={`p-4 border-slate-100 cursor-pointer hover:shadow-elev-2 transition ${openTicket?.id === t.id ? "ring-2 ring-[#F58220]" : ""}`}
              >
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0">
                    <p className="font-semibold text-[#1F3A6B]">{t.title}</p>
                    <p className="text-sm text-slate-600 mt-1 line-clamp-2">{t.comment || "No comment provided."}</p>
                    <p className="text-xs text-slate-500 mt-1.5">
                      {t.respondent_email || "anonymous"} · {new Date(t.created_at).toLocaleString()}
                    </p>
                  </div>
                  <div className="flex flex-col items-end gap-1.5 shrink-0">
                    <Badge className={STATUS_TONE[t.status]}>{STATUS_LABEL[t.status]}</Badge>
                    <span className={`text-[10px] uppercase font-bold tracking-wider ${
                      t.priority === "high" ? "text-rose-600" : t.priority === "medium" ? "text-amber-600" : "text-slate-500"
                    }`}>{t.priority}</span>
                  </div>
                </div>
              </Card>
            ))}
          </div>

          {/* Detail */}
          <div className="lg:sticky lg:top-6 h-fit">
            {openTicket ? (
              <Card className="p-5 border-slate-100">
                <div className="flex items-start justify-between gap-3">
                  <div>
                    <p className="text-xs uppercase tracking-wider text-slate-500 font-semibold">Ticket</p>
                    <h3 className="font-extrabold text-[#1F3A6B] text-lg">{openTicket.title}</h3>
                  </div>
                  <Badge className={STATUS_TONE[openTicket.status]}>{STATUS_LABEL[openTicket.status]}</Badge>
                </div>
                <dl className="mt-4 grid grid-cols-3 gap-y-2 text-sm">
                  <dt className="text-slate-500 col-span-1">Customer</dt><dd className="col-span-2">{openTicket.respondent_name || "—"}</dd>
                  <dt className="text-slate-500 col-span-1">Email</dt><dd className="col-span-2">{openTicket.respondent_email || "—"}</dd>
                  <dt className="text-slate-500 col-span-1">Score</dt><dd className="col-span-2 font-bold text-rose-600">{openTicket.nps_score ?? "—"}</dd>
                  <dt className="text-slate-500 col-span-1">Priority</dt><dd className="col-span-2 uppercase font-semibold tracking-wider">{openTicket.priority}</dd>
                  <dt className="text-slate-500 col-span-1">Created</dt><dd className="col-span-2">{new Date(openTicket.created_at).toLocaleString()}</dd>
                </dl>

                <div className="mt-4">
                  <p className="text-xs uppercase tracking-wider text-slate-500 font-semibold">Comment</p>
                  <p className="mt-1 text-sm text-slate-700 bg-slate-50 border border-slate-100 rounded-md p-3">{openTicket.comment || "No comment provided."}</p>
                </div>

                <div className="mt-5 flex flex-wrap gap-2">
                  {STATUSES.map((s) => (
                    <Button
                      key={s}
                      data-testid={`ticket-set-${s}`}
                      size="sm"
                      variant={openTicket.status === s ? "default" : "outline"}
                      disabled={busy}
                      onClick={() => update(openTicket.id, { status: s })}
                      className={openTicket.status === s ? "bg-[#F58220] hover:bg-[#E36F0E] text-white" : ""}
                    >
                      {STATUS_LABEL[s]}
                    </Button>
                  ))}
                </div>

                <div className="mt-5">
                  <p className="text-xs uppercase tracking-wider text-slate-500 font-semibold">Notes</p>
                  <ul className="mt-2 space-y-2">
                    {(openTicket.notes || []).length === 0 && <li className="text-sm text-slate-500">No notes yet.</li>}
                    {(openTicket.notes || []).map((n) => (
                      <li key={n.id} className="text-sm border border-slate-100 rounded-md p-2.5">
                        <p className="text-xs text-slate-500">{n.by_name} · {new Date(n.at).toLocaleString()}</p>
                        <p className="mt-1 text-slate-700">{n.text}</p>
                      </li>
                    ))}
                  </ul>
                  <Textarea data-testid="ticket-note-input" rows={2} placeholder="Add internal note…" value={note} onChange={(e) => setNote(e.target.value)} className="mt-3"/>
                  <Button data-testid="ticket-note-add" disabled={busy || !note.trim()} onClick={addNote} className="mt-2 bg-[#F58220] hover:bg-[#E36F0E] text-white">Add note</Button>
                </div>

                {err && <p className="mt-3 text-sm text-rose-600">{err}</p>}
              </Card>
            ) : (
              <Card className="p-8 border-dashed border-2 border-slate-200 text-center text-sm text-slate-500">
                Select a ticket to manage it.
              </Card>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

function Stat({ label, value, icon, tone, ...rest }) {
  const tones = {
    rose: "bg-rose-50 text-rose-700 border-rose-100",
    amber: "bg-amber-50 text-amber-700 border-amber-100",
    emerald: "bg-emerald-50 text-emerald-700 border-emerald-100",
    brand: "bg-[#FFF0DF] text-[#1F3A6B] border-[#FFE1BE]",
  };
  return (
    <Card {...rest} className={`p-4 border ${tones[tone]}`}>
      <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-wider opacity-80">
        {icon} {label}
      </div>
      <p className="mt-1 text-3xl font-extrabold tabular-nums">{value}</p>
    </Card>
  );
}
